import { captureException } from '@sentry/nextjs';
import { nanoid } from 'nanoid';
import { ErrorInfo } from 'react';
import { ZodError } from 'zod';

import { isDev } from '@/utils/environment';
import { prepareFingerprint } from '@/utils/errors';

import { ApiError, ApiSellAmountTooSmallError } from './api';
import { ClientError } from './client';
import {
  JupiterApiError,
  JupiterFetchRoutesError,
  JupiterTokenNotTradableError,
  JupiterUncategorizedError,
} from './jupiter';

type BaseErrorProperties = {
  /** Unique error ID */
  error_id: string;
  /** The error message */
  message: string;
  /** The type of error */
  type_of_error: string;
};

type ApiErrorProperties = BaseErrorProperties & {
  /** Request ID */
  zid?: string;
  /** Additional context */
  [key: string]: unknown;
};

type ErrorProperties = ApiErrorProperties | BaseErrorProperties;

/**
 * Reports an error to Sentry. The Amplitude session replay link and device/session
 * ids are attached to every Sentry event globally in `beforeSend`
 * (`instrumentation-client.ts`), so callers here do not need to wire them in.
 *
 * @param error - The error to report.
 * @param eventName - The name of the error event.
 * @param errorInfo - Stack-trace information (only used by Sentry)
 */
export const reportError = (
  error: ApiError | JupiterApiError | ZodError<unknown> | Error,
  eventName: string,
  errorInfo?: ErrorInfo,
) => {
  let fingerprint: string[] | undefined;
  let properties: ErrorProperties = {
    error_id: nanoid(10),
    message: error.message,
    type_of_error: 'uncategorized',
  };

  if (error instanceof ApiError) {
    const apiProperties: ApiErrorProperties = {
      ...properties,
      message: error.details.message,
      type_of_error: error.details.name.toLowerCase(),
      zid: typeof error.details.data?.zid === 'string' ? error.details.data.zid : undefined,
      ...error.context,
    };

    properties = apiProperties;
  } else if (error instanceof ClientError) {
    properties.type_of_error = error.type;
    fingerprint = prepareFingerprint(error);
  } else if (error instanceof Error) {
    fingerprint = prepareFingerprint(error);
  }

  // Note: Error events are not tracked in Ampli, only in Sentry

  // Don't report ApiError's to Sentry, as they are unactionable
  if (shouldNotReportToSentry(error)) return;

  captureException(error, {
    contexts: {
      matchaContext: { name: eventName, ...properties },
    },
    extra: {
      errorInfo,
    },
    fingerprint,
    tags: {
      'app.error_id': properties.error_id,
      'app.error_name': eventName,
      'app.type_of_error': properties.type_of_error,
    },
  });
};

const shouldNotReportToSentry = (
  error: ApiError | JupiterApiError | ZodError<unknown> | Error,
): boolean =>
  error instanceof ApiError ||
  error instanceof JupiterTokenNotTradableError ||
  error instanceof JupiterFetchRoutesError ||
  error instanceof JupiterUncategorizedError;

/**
 * Determines if an error should not be reported.
 */
export const shouldIgnore = (
  error: ApiError | JupiterApiError | ZodError<unknown> | Error,
): boolean => isDev || error instanceof ApiSellAmountTooSmallError;
